import { useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { cn } from '@/shared/utils/utils';
import { CapabilityGate } from '../CapabilityGate';
import { useAICapability } from '../../hooks/useAICapability';
import { useAIChat, useMeetingAgenda } from '../../hooks/useAIGenerative';
import { useMeetings } from '@/features/meetings/hooks/useMeetings';
import type { ChatMessage } from '../../types';

// ─── Assistant (chat) ─────────────────────────────────────────────────────────

/** Client-side quick-start prompts. Clicking one sends a real /api/ai/chat call. */
const STARTER_PROMPTS = [
  'Summarize my pipeline health right now',
  'Which leads should I follow up with today?',
  'Draft a follow-up message for a warm lead',
  'What are my most important tasks this week?',
];

export function AssistantSection() {
  const { state, refetch, isFetching } = useAICapability();
  const chat = useAIChat();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const sendText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || chat.isPending) return;
    const history = [...messages];
    setMessages((m) => [...m, { role: 'user', content: trimmed }]);
    setInput('');
    chat.mutate(
      { message: trimmed, history },
      {
        onSuccess: (reply) => {
          setMessages((m) => [...m, { role: 'assistant', content: reply }]);
          requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }));
        },
      },
    );
  };

  const send = () => sendText(input);

  return (
    <Card className="flex h-[600px] flex-col">
      <CardHeader title="AI Assistant" />
      <CardBody className="flex min-h-0 flex-1 flex-col">
        <CapabilityGate state={state} onRetry={() => refetch()} retrying={isFetching}>
          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto pb-3">
            {messages.length === 0 ? (
              <div className="flex flex-col gap-4">
                <EmptyState title="Ask the assistant" description="Ask about your leads, deals, tasks or pipeline." />
                <div className="flex flex-col gap-2">
                  <p className="text-label-caps uppercase text-on-surface-variant">Try asking</p>
                  <div className="flex flex-wrap gap-2">
                    {STARTER_PROMPTS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => sendText(p)}
                        className="rounded-full border border-outline px-3 py-1.5 text-body-sm text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[80%] whitespace-pre-wrap rounded-card px-3 py-2 text-data-md',
                      m.role === 'user' ? 'bg-primary text-on-primary' : 'bg-surface-variant text-on-surface',
                    )}
                  >
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {chat.isPending && (
              <div className="flex items-center gap-2 text-body-sm text-on-surface-variant">
                <Spinner /> Thinking…
              </div>
            )}
          </div>
          <div className="mt-auto flex items-center gap-2 border-t border-outline pt-3">
            <Input
              name="ai-chat-input"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
            />
            <Button onClick={send} loading={chat.isPending} size="icon" aria-label="Send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </CapabilityGate>
      </CardBody>
    </Card>
  );
}

// ─── Meeting Assistant (agenda generator) ─────────────────────────────────────
export function MeetingAssistantSection() {
  const { state, refetch, isFetching } = useAICapability();
  const { data: meetings = [] } = useMeetings();
  const agenda = useMeetingAgenda();
  const [meetingId, setMeetingId] = useState('');

  const options = [
    { value: '', label: 'Select a meeting…' },
    ...meetings.map((m) => ({ value: m.id, label: m.title })),
  ];

  return (
    <Card>
      <CardHeader title="Meeting Assistant" />
      <CardBody className="space-y-4">
        <CapabilityGate state={state} onRetry={() => refetch()} retrying={isFetching}>
          <div className="flex items-end gap-2">
            <div className="w-72">
              <Select
                label="Meeting"
                name="ai-meeting"
                value={meetingId}
                onChange={(e) => setMeetingId(e.target.value)}
                options={options}
              />
            </div>
            <Button disabled={!meetingId} loading={agenda.isPending} onClick={() => agenda.mutate(meetingId)}>
              Generate Agenda
            </Button>
          </div>

          {agenda.isPending ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : agenda.data ? (
            <div className="whitespace-pre-wrap rounded-card border border-outline bg-surface p-4 text-data-md text-on-surface">
              {agenda.data}
            </div>
          ) : agenda.isSuccess && agenda.data === null ? (
            <EmptyState
              title="Generative AI is currently unavailable"
              description="The agenda could not be generated because the AI provider is not connected. Try again once it is configured."
            />
          ) : null}
        </CapabilityGate>
      </CardBody>
    </Card>
  );
}
